/**
 * ThemeContext - Sistema simple de temas (Claro, Oscuro, Sistema)
 */
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // El tema de marca es CLARO (Terminal de Trading) con la identidad Oro/Plata.
    // Migración: revertir a CLARO a los usuarios que fueron forzados a oscuro en la
    // versión previa. Se ejecuta una sola vez; después se respeta el cambio manual.
    try {
      const revertedToLight = localStorage.getItem('kap_theme_default_light_v2');
      if (!revertedToLight) {
        localStorage.setItem('kap_theme_default_light_v2', '1');
        localStorage.setItem('theme', 'light');
        return 'light';
      }
    } catch { /* noop */ }
    const saved = localStorage.getItem('theme');
    return saved || 'light'; // Por defecto el tema claro (Terminal de Trading)
  });

  const [resolvedTheme, setResolvedTheme] = useState('light');

  // Acento de marca: 'gold' (por defecto) | 'silver'. Cambia la variable CSS --accent
  // y toda la paleta gold-* de Tailwind (que apunta a var(--gold-*)) en 1 clic.
  const [accent, setAccent] = useState(() => {
    try { return localStorage.getItem('kap_accent') || 'gold'; } catch { return 'gold'; }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (accent && accent !== 'gold') {
      root.setAttribute('data-accent', accent);
    } else {
      root.removeAttribute('data-accent');
    }
    try { localStorage.setItem('kap_accent', accent); } catch { /* noop */ }
  }, [accent]);


  useEffect(() => {
    const root = document.documentElement;
    
    const applyTheme = () => {
      let actualTheme = theme;
      
      if (theme === 'system') {
        actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      
      setResolvedTheme(actualTheme);
      
      // Quitar todas las clases de tema
      root.classList.remove('light', 'dark');
      
      // Aplicar la clase del tema
      root.classList.add(actualTheme);
      
      localStorage.setItem('theme', theme);
    };

    applyTheme();

    // Escuchar cambios en preferencia del sistema
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        applyTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Memoizar el objeto value para evitar re-renders innecesarios
  const value = useMemo(() => ({
    theme,
    setTheme,
    resolvedTheme,
    accent,
    setAccent
  }), [theme, resolvedTheme, accent]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
